import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { DollarSign, Mail, Users, Sparkles, UserCog, Send, Clock, TrendingUp, CalendarDays, Activity, Wallet } from "lucide-react";
import { Card, Kpi, PageHeader, RangeFilter, Spinner, Avatar, StatusBadge, Table, Td, Empty, fmtDur, fmtHours } from "@/components/common";
import { ShiftPanel } from "@/components/work/ShiftPanel";
import { RevenueChart, HBar, LineSeries } from "@/components/charts";
import { UnreadByProfile } from "@/components/UnreadByProfile";
import { PaidByProfile } from "@/components/PaidByProfile";
import { BotToday } from "@/components/BotToday";
import { JoinDashboardCard } from "@/components/inbox/JoinRequests";
import { LifetimeCard } from "@/components/LifetimeCard";
import { useAuth, useLive } from "@/context/AppContext";
import { useLiveAccounts } from "@/lib/useLiveAccounts";
import { api, rangeParams, toastErr } from "@/lib/api";
import { money, num, ago, fmtDateTime, isStaff, ROLE_LABEL } from "@/lib/format";
import { StaffAlertsSection, PunctualityTable, ResponseTimes, fmtResp } from "@/components/StaffLive";
import { OnShift } from "@/components/OnShiftCards";

export function RecentSales({ items, testid = "recent-sales" }) {
  if (!items?.length) return <Empty title="Nessuna vendita" />;
  return (
    <Table head={["Data", "Tipo", "Importo", "Fan", "Creator", "Chatter", "Account"]} testid={testid}>
      {items.map((s) => (
        <tr key={s.id}>
          <Td className="text-slate-500">{fmtDateTime(s.created_at)}</Td>
          <Td><span className={`text-[10px] font-bold rounded px-1.5 py-0.5 ${s.type === "ppv" ? "bg-violet-500/15 text-violet-300" : s.type === "tip" ? "bg-emerald-500/15 text-emerald-300" : "bg-blue-500/15 text-blue-300"}`}>{s.type.toUpperCase()}</span></Td>
          <Td className="num text-emerald-400 font-semibold">{money(s.amount)}</Td>
          <Td>@{s.fan_username}</Td><Td>{s.creator_name}</Td><Td>{s.chatter_name || "—"}</Td><Td className="text-slate-500">@{s.account_username}</Td>
        </tr>
      ))}
    </Table>
  );
}

export function RecentShifts({ testid = "recent-shifts" }) {
  const [items, setItems] = useState(null);
  const load = useCallback(() => api.get("/work/entries", { params: { range: "30d", limit: 10, metrics: true } }).then((r) => setItems(r.data.items)).catch(toastErr), []);
  useEffect(() => { load(); }, [load]);
  useLive(load);
  if (!items) return <Spinner />;
  if (!items.length) return <Empty title="Nessun turno registrato" />;
  const t = (d) => (d ? new Date(d).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }) : "—");
  return (
    <Table head={["Chatter / data", "Creator", "Previsto", "Reale", "Ritardo", "Fatturato", "Guadagno", "Risposta media", "Msg inviati", "Pause"]} testid={testid}>
      {items.map((s) => { const m = s.metrics || {}; return (
        <tr key={s.id} data-testid={`recent-shift-${s.id}`}>
          <Td className="font-semibold text-slate-100 !whitespace-normal">{s.user_name}<div className="text-[10px] font-normal text-slate-500">{new Date(s.clock_in).toLocaleDateString("it-IT", { day: "2-digit", month: "short" })}</div></Td>
          <Td className="text-xs !whitespace-normal">{(m.by_creator || []).map((l) => l.name).join(", ") || (s.shift_creator_names || []).join(", ") || "—"}</Td>
          <Td className="num text-xs" data-testid="recent-shift-planned">{m.planned_start ? `${t(m.planned_start)}–${t(m.planned_end)}` : "—"}</Td>
          <Td className="num text-xs !whitespace-normal">{t(s.clock_in)}–{t(s.clock_out)}<div className="text-[10px] text-slate-500">{fmtDur(s.duration_minutes)}</div></Td>
          <Td className={`num text-xs ${m.late_min ? "text-amber-300" : ""}`} data-testid="recent-shift-late">{m.late_min ? `${m.late_min} min` : "—"}</Td>
          <Td className="num text-emerald-400 font-semibold" data-testid="recent-shift-revenue">{money(s.revenue_total)}</Td>
          <Td className="num text-slate-400">{money(s.earnings_total)}</Td>
          <Td className="num text-xs">{fmtResp(m.resp_avg_s)}</Td><Td className="num text-xs">{m.msgs_out ?? "—"}</Td><Td className="num text-xs">{m.pauses_n ? `${m.pauses_n} · ${m.pause_min} min` : "—"}</Td>
        </tr>); })}
    </Table>
  );
}

function Values({ rows, testid }) {
  return (
    <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1" data-testid={testid}>
      {(rows || []).map((r) => <div key={r.id || r.name} className="flex justify-between text-xs border-b border-[#1A1D2B] py-1"><span className="text-slate-300 truncate">{r.name}</span><span className="num text-emerald-400 font-semibold">{money(r.revenue)}</span></div>)}
    </div>
  );
}

const fmtD = (s) => new Date(s).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric", timeZone: "Europe/Rome" });
const LBL = { today: "oggi", yesterday: "ieri", "7d": "ultimi 7 giorni", "30d": "ultimi 30 giorni", "90d": "ultimi 90 giorni", week: "settimana corrente", month: "questo mese", prev_month: "mese scorso", year: "anno corrente", all: "totale" };
// Tile titles follow the selected period (Europe/Rome days)
const periodLabel = (range, custom) => (range === "day" && custom.start ? fmtD(custom.start)
  : range === "custom" && custom.start ? `dal ${fmtD(custom.start)} al ${fmtD(custom.end)}`
    : range.startsWith("m:") ? new Date(`${range.slice(2)}-15`).toLocaleDateString("it-IT", { month: "long", year: "numeric" }) : LBL[range] || "periodo");

function AdminDashboard() {
  const { user } = useAuth();
  const boss = ["owner", "admin", "manager"].includes(user.role);
  const [range, setRange] = useState("today");
  const [custom, setCustom] = useState({});
  const [d, setD] = useState(null);
  const [unreadTot, setUnreadTot] = useState(null);
  const load = useCallback(() => {
    if ((range === "custom" && !(custom.start && custom.end)) || (range === "day" && !custom.start)) return;
    api.get("/dashboard/admin", { params: rangeParams(range, custom) }).then((r) => setD(r.data)).catch(toastErr);
  }, [range, custom]);
  useEffect(() => { load(); }, [load]);
  useLive(load);
  if (!d) return <Spinner />;
  const t = d.counts && d.totals, c = d.counts, gran = ["today", "yesterday"].includes(range) ? "hour" : "day";
  return (
    <div data-testid="admin-dashboard">
      <PageHeader title="Panoramica agenzia" subtitle="DOPAMINE AGENCY LLC · dati aggiornati in tempo reale" actions={<RangeFilter value={range} onChange={setRange} custom={custom} setCustom={setCustom} />} />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
        <Kpi testid="kpi-today-revenue" label={`Fatturato ${periodLabel(range, custom)}`} value={money(d.totals?.revenue)} icon={TrendingUp} />
        <Kpi testid="kpi-month-current-revenue" label="Fatturato mese attuale" value={money(d.revenue_month)} icon={CalendarDays} />
        <Kpi testid="kpi-month-revenue" label={`Turni con vendite ${periodLabel(range, custom)}`} value={num(d.totals?.count)} icon={CalendarDays} />
        <Kpi testid="kpi-total-revenue" label="Fatturato totale" value={money(d.revenue_all)} icon={Activity} />
        <Kpi testid="kpi-unread" label="Chat non lette" value={num(unreadTot ?? c.unread)} icon={Mail} />
        <Kpi testid="kpi-creators" label="Creator attive" value={num(c.active_creators)} icon={Sparkles} />
        <Kpi testid="kpi-chatters" label="Chatter attivi" value={num(c.active_chatters)} icon={UserCog} />
        <Kpi testid="kpi-accounts" label="Account Telegram" value={`${c.connected_accounts}/${c.total_accounts}`} sub="collegati" icon={Send} />
      </div>
      <Card title="Chatter in turno adesso" className="mb-3" testid="on-shift-card"><OnShift /></Card>
      <BotToday />
      {boss && <PunctualityTable />}
      {boss && <StaffAlertsSection />}
      <UnreadByProfile onTotal={setUnreadTot} />
      <PaidByProfile />
      <div className="mb-3"><ResponseTimes /></div>
      <JoinDashboardCard />
      <div className="grid lg:grid-cols-2 gap-3 mb-3">
        <Card title="Fatturato nel tempo" testid="chart-revenue"><RevenueChart data={d.series} gran={gran} /></Card>
        <Card title="Fatturato per creator" testid="chart-by-creator"><HBar data={d.by_creator} /><Values rows={d.by_creator} testid="values-by-creator" /></Card>
        <Card title="Fatturato per chatter" testid="chart-by-chatter"><HBar data={d.by_chatter} /><Values rows={d.by_chatter} testid="values-by-chatter" /></Card>
      </div>
      <Card title="Turni recenti"><RecentShifts /></Card>
    </div>
  );
}

function ChatterDashboard() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const accs = useLiveAccounts();
  const [range, setRange] = useState("today");
  const [custom, setCustom] = useState({});
  const [p, setP] = useState(null);
  const load = useCallback(() => {
    api.get("/dashboard/chatter").then((r) => setD(r.data)).catch(toastErr);
  }, []);
  const loadP = useCallback(() => { if (range === "custom" && !(custom.start && custom.end)) return; api.get(`/stats/chatter/${user.id}`, { params: rangeParams(range, custom) }).then((r) => setP(r.data.period)).catch(toastErr); }, [range, custom, user.id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadP(); }, [loadP]);
  useLive(() => { load(); loadP(); });
  if (!d) return <Spinner />;
  return (
    <div data-testid="chatter-dashboard">
      <PageHeader title={`Ciao ${user.first_name}`} subtitle="I tuoi risultati" actions={<RangeFilter value={range} onChange={setRange} custom={custom} setCustom={setCustom} testid="my-period" />} />
      <ShiftPanel showRevenue />
      <UnreadByProfile />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3" data-testid="my-period-kpis">
        <Kpi testid="kpi-my-sales-today" label={range === "today" ? "Fatturato oggi" : "Fatturato periodo"} value={money(p?.revenue)} icon={DollarSign} />
        <Kpi testid="kpi-my-earnings" label={range === "today" ? "Guadagno di oggi" : "Guadagno periodo"} value={money(p?.earnings)} icon={Wallet} />
        <Kpi testid="kpi-my-hours" label="Ore lavorate" value={fmtHours(p?.hours)} icon={Clock} />
      </div>
      <div className="grid lg:grid-cols-3 gap-3 mb-3">
        <Card title="I miei account Telegram" testid="my-accounts">
          <div className="space-y-2">
            {accs.map((a) => (
              <Link key={a.id} to={`/messages?a=${a.id}`} className="flex items-center gap-3 rounded-md p-2 hover:bg-white/5" data-testid={`my-account-${a.username}`}>
                <Avatar name={a.username || a.label} url={a.avatar_url} size={32} />
                <div className="flex-1 min-w-0"><div className="text-sm font-semibold truncate">@{a.username}</div><div className="text-xs text-slate-500">{a.creator_name}</div></div>
                {a.unread > 0 ? <span className="num text-xs rounded-full bg-violet-600 px-2 py-0.5">{a.unread} chat non lette</span> : <StatusBadge status={a.status} />}
              </Link>
            ))}
            {!accs.length && <Empty title="Nessun account assegnato" />}
          </div>
        </Card>
        <Card title="Le mie performance · ultimi 30 giorni" className="lg:col-span-2" testid="my-performance"><RevenueChart data={d.performance} /></Card>
      </div>
      <LifetimeCard uid={user.id} />
      <Card title="I miei turni recenti"><RecentShifts testid="my-recent-shifts" /></Card>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  return isStaff(user) ? <AdminDashboard /> : <ChatterDashboard />;
}
